import React from "react";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import cap from "./capacity.json";

// Read-only visual forecast. Full-day plan from first hour, targets only
// (impressionsactual ignored). Capacity per hour is shared by all orders
// scheduled in that hour; allocation is water-filling by % of target
// (engine serves the order furthest from 100%).

const HOURS = [];
for (let h = cap.firstHour; h <= cap.lastHour; h++) HOURS.push(h);

function capacityFor(day, hour) {
  const row = cap.days[String(day)];
  const idx = Math.max(hour, cap.hours[0]) - cap.hours[0];
  return row[idx] + cap.extra;
}

// today in SAST, same convention as manage.js
function todayInfo() {
  let d = new Date(Date.now());
  d.setHours(d.getHours() + 2);
  const iso = d.toISOString().substr(0, 10);
  return { iso, day: new Date(iso + "T00:00:00Z").getUTCDay() };
}

function collectOrders(labels, today, dups) {
  const out = [];
  (labels || []).forEach((a) =>
    a.clients.items.forEach((b) =>
      b.campaigns.items.forEach((c) =>
        c.orders.items.forEach((d) => {
          if (!d.active) return;
          let dhi = [];
          try {
            dhi = JSON.parse(d.dayhourindicator) || [];
          } catch (e) {
            return;
          }
          const sameDay = dhi.filter((z) => z.date === today);
          const counts = {};
          dhi.forEach((z) => (counts[z.date] = (counts[z.date] || 0) + 1));
          Object.keys(counts)
            .sort()
            .forEach((dt) => {
              if (counts[dt] > 1)
                dups.push({ date: dt, name: `${b.name}-${c.name}-${d.name}` });
            });
          const entries = sameDay.filter((z) => parseInt(z.impressionstarget) > 0);
          if (entries.length === 0) return;
          // duplicates: use the lowest target
          const e = entries.reduce((m, z) =>
            parseInt(z.impressionstarget) < parseInt(m.impressionstarget)
              ? z
              : m
          );
          out.push({
            id: d.id,
            name: `${b.name} – ${c.name} – ${d.name}`,
            target: parseInt(e.impressionstarget),
            hours: e.hours || [],
            delivered: 0,
            cells: {},
          });
        })
      )
    )
  );
  return out.sort((x, y) => parseInt(x.id) - parseInt(y.id));
}

// Raise all eligible orders to a common % level, capped at 100%.
function waterFill(eligible, capacity) {
  const alloc = (L) =>
    eligible.map((o) =>
      Math.max(0, Math.min(L * o.target - o.delivered, o.target - o.delivered))
    );
  const sum = (arr) => arr.reduce((s, v) => s + v, 0);
  if (sum(alloc(1)) <= capacity) return alloc(1);
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (sum(alloc(mid)) > capacity) hi = mid;
    else lo = mid;
  }
  return alloc(lo);
}

function simulate(labels) {
  const { iso, day } = todayInfo();
  const dups = [];
  const orders = collectOrders(labels, iso, dups);
  const totals = {};
  const caps = {};
  HOURS.forEach((h) => {
    caps[h] = capacityFor(day, h);
    totals[h] = 0;
    const eligible = orders.filter(
      (o) => o.hours.includes(h) && o.delivered < o.target
    );
    if (eligible.length === 0) return;
    const a = waterFill(eligible, caps[h]);
    eligible.forEach((o, i) => {
      o.cells[h] = a[i];
      o.delivered += a[i];
      totals[h] += a[i];
    });
  });
  return { iso, day, orders, totals, caps, dups };
}

const k = (v) => (v > 0 ? Math.ceil(v / 1000) + "k" : "");

class HourlyForecast extends React.Component {
  state = { open: false };
  render() {
    const sim = this.state.open ? simulate(this.props.labels) : null;
    const cell = { padding: "4px 6px", textAlign: "center", fontSize: 15, border: "1px solid #9e9e9e" };
    const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return (
      <Paper style={{ padding: 10, background: "#eee", color: "#666", marginBottom: 20 }}>
        <div
          style={{ cursor: "pointer", fontWeight: "bold" }}
          onClick={() => this.setState({ open: !this.state.open })}
        >
          {this.state.open ? "▼" : "▶"} Today's delivery forecast
        </div>
        {sim && (
          <div style={{ overflowX: "auto" }}>
            <div style={{ fontSize: 15, margin: "6px 0" }}>
              {sim.iso} ({day[sim.day]}) · maximum capacity, no segmentation:
              shortfalls are certain, "on target" is best case. Read-only.
            </div>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ ...cell, textAlign: "left" }}>Order</TableCell>
                  {HOURS.map((h) => (
                    <TableCell key={h} style={cell}>{h}</TableCell>
                  ))}
                  <TableCell style={cell}>Capacity allocation</TableCell>
                  <TableCell style={cell}>%</TableCell>
                  <TableCell style={cell}>Target</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sim.orders.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell style={{ ...cell, textAlign: "left", whiteSpace: "nowrap" }}>
                      {o.name}
                    </TableCell>
                    {HOURS.map((h) => (
                      <TableCell
                        key={h}
                        style={{
                          ...cell,
                          background: o.hours.includes(h) ? "#c8e6c9" : "transparent",
                        }}
                      >
                        {k(o.cells[h] || 0)}
                      </TableCell>
                    ))}
                    <TableCell style={cell}>{k(o.delivered)}</TableCell>
                    <TableCell style={cell}>
                      {Math.floor((o.delivered * 100) / o.target)}%
                    </TableCell>
                    <TableCell style={cell}>{k(o.target)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell style={{ ...cell, textAlign: "left", fontWeight: "bold" }}>
                    Total delivered
                  </TableCell>
                  {HOURS.map((h) => (
                    <TableCell key={h} style={{ ...cell, fontWeight: "bold" }}>
                      {k(sim.totals[h])}
                    </TableCell>
                  ))}
                  <TableCell style={cell} colSpan={3} />
                </TableRow>
                <TableRow>
                  <TableCell style={{ ...cell, textAlign: "left", fontWeight: "bold" }}>
                    Total capacity
                  </TableCell>
                  {HOURS.map((h) => (
                    <TableCell key={h} style={{ ...cell, fontWeight: "bold" }}>
                      {k(sim.caps[h])}
                    </TableCell>
                  ))}
                  <TableCell style={cell} colSpan={3} />
                </TableRow>
              </TableBody>
            </Table>
            {sim.dups.map((x) => (
              <div key={x.date + x.name} style={{ fontSize: 15, marginTop: 6 }}>
                {`you have duplicate date entries for ${x.date} for ${x.name}`}
              </div>
            ))}
          </div>
        )}
      </Paper>
    );
  }
}

export default HourlyForecast;
